import { useMemo, useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabaseClient'
import { C, st, formatDate } from '../../theme'
import { PageHeader } from '../../components/UI'
import { useAppPrefs } from '../../AppPrefs'
import { makeT } from '../../i18n'

// ── Helpers ──────────────────────────────────────────────────────────────────
const fmt = n => (n ?? 0).toLocaleString()
const pct = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0)
const monthKey = d => (d || '').slice(0, 7)
const monthLabel = k => { const [y, m] = (k || '').split('-'); return y ? `${y.slice(2)}/${+m}月` : '' }

function getLast6Months() {
  const out = []
  for (let i = 5; i >= 0; i--) {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i)
    out.push(d.toISOString().slice(0, 7))
  }
  return out
}
function getLast12Months() {
  const out = []
  for (let i = 11; i >= 0; i--) {
    const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - i)
    out.push(d.toISOString().slice(0, 7))
  }
  return out
}

function exportCSV(filename, headers, rows) {
  const csv = [headers, ...rows]
    .map(r => r.map(v => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(','))
    .join('\n')
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

// ── Chart Components ──────────────────────────────────────────────────────────

// Dual-color bar chart (入庫=green, 出庫=red)
function DualBarChart({ data, height = 160, noDataText = '尚無資料' }) {
  const maxVal = Math.max(...data.flatMap(d => [d.in || 0, d.out || 0]), 1)
  if (!data.length) return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, fontSize: 13 }}>{noDataText}</div>
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height }}>
        {data.map((d, i) => (
          <div key={i} title={`${d.label || d.dateStr}\n入庫: ${d.in}　出庫: ${d.out}`}
            style={{ flex: 1, display: 'flex', alignItems: 'flex-end', gap: 1, cursor: 'default', minWidth: 0 }}>
            <div style={{ flex: 1, background: '#10b981', borderRadius: '3px 3px 0 0', height: `${(d.in || 0) / maxVal * 100}%`, minHeight: d.in > 0 ? 2 : 0, transition: 'height 0.3s' }} />
            <div style={{ flex: 1, background: '#ef4444', borderRadius: '3px 3px 0 0', height: `${(d.out || 0) / maxVal * 100}%`, minHeight: d.out > 0 ? 2 : 0, transition: 'height 0.3s' }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6 }}>
        {[0, Math.floor(data.length / 2), data.length - 1].filter((v, i, a) => a.indexOf(v) === i).map(i => (
          data[i] && <span key={i} style={{ fontSize: 9, color: C.muted }}>{(data[i].label || data[i].dateStr || '').slice(-5)}</span>
        ))}
      </div>
    </div>
  )
}

// Single-color vertical bar chart with labels
function ColBarChart({ data, color = C.accent, height = 130, showValue = true, noDataText = '尚無資料' }) {
  const maxVal = Math.max(...data.map(d => d.value || 0), 1)
  if (!data.length) return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, fontSize: 13 }}>{noDataText}</div>
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height }}>
        {data.map((d, i) => (
          <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, height: '100%', justifyContent: 'flex-end' }}>
            {showValue && d.value > 0 && <div style={{ fontSize: 9, color, fontWeight: 700 }}>{d.value > 9999 ? `${(d.value/1000).toFixed(1)}k` : d.value}</div>}
            <div style={{ width: '100%', background: color, borderRadius: '4px 4px 0 0', height: `${(d.value || 0) / maxVal * 100}%`, minHeight: d.value > 0 ? 4 : 0, transition: 'height 0.4s', opacity: 0.85 }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
        {data.map((d, i) => (
          <div key={i} style={{ flex: 1, textAlign: 'center', fontSize: 9, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.label}</div>
        ))}
      </div>
    </div>
  )
}

// Donut chart via conic-gradient
function DonutChart({ segments, centerLabel = '總計' }) {
  const total = segments.reduce((a, s) => a + (s.value || 0), 0)
  let cum = 0
  const grad = total > 0
    ? segments.map(s => { const from = cum; cum += (s.value / total) * 360; return `${s.color} ${from.toFixed(1)}deg ${cum.toFixed(1)}deg` }).join(', ')
    : '#e2e8f0 0deg 360deg'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
      <div style={{ position: 'relative', width: 96, height: 96, flexShrink: 0 }}>
        <div style={{ width: 96, height: 96, borderRadius: '50%', background: `conic-gradient(${grad})` }} />
        <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 58, height: 58, borderRadius: '50%', background: '#fff', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: 17, fontWeight: 900, color: C.text, lineHeight: 1 }}>{total}</div>
          <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>{centerLabel}</div>
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1, minWidth: 100 }}>
        {segments.map(s => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: s.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: C.text, flex: 1 }}>{s.label}</span>
            <span style={{ fontSize: 12, fontWeight: 800, color: s.color }}>{s.value}</span>
            <span style={{ fontSize: 10, color: C.muted, width: 32, textAlign: 'right' }}>{pct(s.value, total)}%</span>
          </div>
        ))}
      </div>
    </div>
  )
}

// Horizontal rank bars
function RankBars({ items, color = C.accent, unit = '', noDataText = '尚無資料' }) {
  const maxVal = Math.max(...items.map(i => i.value), 1)
  if (!items.length) return <div style={{ color: C.muted, fontSize: 13, padding: '12px 0' }}>{noDataText}</div>
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {items.map((item, i) => (
        <div key={item.label || i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: 11, color: i < 3 ? color : C.muted, fontWeight: i < 3 ? 800 : 400, width: 18, textAlign: 'right', flexShrink: 0 }}>#{i + 1}</div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontSize: 12, color: C.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '68%' }}>{item.label}</span>
              <span style={{ fontSize: 12, fontWeight: 800, color }}>{fmt(item.value)}{unit}</span>
            </div>
            <div style={{ background: '#f1f5f9', borderRadius: 4, height: 7, overflow: 'hidden' }}>
              <div style={{ width: `${(item.value / maxVal) * 100}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.5s' }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

// Month vs last month delta badge
function DeltaBadge({ curr, prev }) {
  if (prev === undefined || prev === null) return null
  const diff = curr - prev
  if (diff === 0) return <span style={{ fontSize: 11, color: C.muted, marginLeft: 6 }}>≈ 持平</span>
  const p = prev > 0 ? Math.abs(Math.round((diff / prev) * 100)) : null
  return (
    <span style={{ fontSize: 11, fontWeight: 700, color: diff > 0 ? '#10b981' : '#ef4444', marginLeft: 6 }}>
      {diff > 0 ? '↑' : '↓'} {p !== null ? `${p}%` : fmt(Math.abs(diff))}
    </span>
  )
}

// ── Section card wrapper ──────────────────────────────────────────────────────
const SectionCard = ({ title, subtitle, action, children }) => (
  <div style={st.card}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
      <div>
        <div style={{ fontSize: 15, fontWeight: 800, color: C.text }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{subtitle}</div>}
      </div>
      {action}
    </div>
    {children}
  </div>
)

// ── Legend row ────────────────────────────────────────────────────────────────
const Legend = ({ items }) => (
  <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
    {items.map(({ color, label }) => (
      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
        <div style={{ width: 12, height: 12, background: color, borderRadius: 3 }} />
        <span style={{ fontSize: 12, color: C.sub }}>{label}</span>
      </div>
    ))}
  </div>
)

// ═══════════════════════════════════════════════════════════════════════════════
// Main Component
// ═══════════════════════════════════════════════════════════════════════════════
export default function Reports({ products, records, userProfile }) {
  const { lang } = useAppPrefs()
  const _ = makeT(lang)

  const [tab, setTab] = useState('overview')

  // ── DB data ──
  const [orders,       setOrders]       = useState([])
  const [shipments,    setShipments]    = useState([])
  const [pos,          setPos]          = useState([])   // purchase_orders
  const [maintenance,  setMaintenance]  = useState([])   // maintenance records
  const [dbLoading, setDbLoading] = useState(true)

  // ── Trend tab config ──
  const [trendDays,     setTrendDays]     = useState(30)
  const [trendRecords,  setTrendRecords]  = useState(null)  // null = use prop
  const [trendLoading,  setTrendLoading]  = useState(false)

  // ── Load DB data on mount ──
  useEffect(() => {
    const cid = userProfile?.company_id
    let woQ = supabase.from('work_orders').select('*').order('created_at', { ascending: false }).limit(500)
    let shQ = supabase.from('shipments').select('id, shipment_no, customer_name, status, created_at').order('created_at', { ascending: false }).limit(500)
    let poQ = supabase.from('purchase_orders').select('id, po_no, status, created_at').order('created_at', { ascending: false }).limit(500)
    let mtQ = supabase.from('maintenance').select('id, machine_name, machine_code, type, status, is_stopped, cost, report_date, created_at').order('created_at', { ascending: false }).limit(500)
    if (cid) { woQ = woQ.eq('company_id', cid); shQ = shQ.eq('company_id', cid); poQ = poQ.eq('company_id', cid); mtQ = mtQ.eq('company_id', cid) }
    Promise.all([woQ, shQ, poQ, mtQ]).then(([wo, sh, po, mt]) => {
      if (wo.data) setOrders(wo.data)
      if (sh.data) setShipments(sh.data)
      if (po.data) setPos(po.data)
      if (mt.data) setMaintenance(mt.data)
      setDbLoading(false)
    })
  }, [userProfile?.company_id])

  // ── Load trend records from DB when range changes ──
  const loadTrend = useCallback(async (days) => {
    setTrendLoading(true)
    const since = new Date()
    since.setDate(since.getDate() - days)
    const cid = userProfile?.company_id
    let q = supabase.from('records').select('type, qty, created_at').gte('created_at', since.toISOString()).order('created_at', { ascending: true })
    if (cid) q = q.eq('company_id', cid)
    const { data } = await q
    setTrendRecords(data || [])
    setTrendLoading(false)
  }, [userProfile?.company_id])

  useEffect(() => {
    if (tab === 'trend') loadTrend(trendDays)
  }, [tab, trendDays])

  // ── Use fresh records if loaded, else fall back to prop ──
  const activeRecords = trendRecords !== null ? trendRecords : records

  // ── Overview stats ────────────────────────────────────────────────────────
  const now = new Date()
  const thisMonth = now.toISOString().slice(0, 7)
  const lastMonthDate = new Date(now); lastMonthDate.setMonth(lastMonthDate.getMonth() - 1)
  const lastMonth = lastMonthDate.toISOString().slice(0, 7)

  const overview = useMemo(() => {
    const totalStock = products.reduce((a, p) => a + p.stock, 0)
    const lowStock   = products.filter(p => p.stock <= p.safe_stock)

    const recIn  = records.filter(r => r.type === 'in')
    const recOut = records.filter(r => r.type === 'out')

    const thisIn  = recIn.filter(r => r.created_at?.slice(0, 7) === thisMonth).reduce((a, r) => a + r.qty, 0)
    const lastIn  = recIn.filter(r => r.created_at?.slice(0, 7) === lastMonth).reduce((a, r) => a + r.qty, 0)
    const thisOut = recOut.filter(r => r.created_at?.slice(0, 7) === thisMonth).reduce((a, r) => a + r.qty, 0)
    const lastOut = recOut.filter(r => r.created_at?.slice(0, 7) === lastMonth).reduce((a, r) => a + r.qty, 0)

    const thisShip = shipments.filter(s => s.created_at?.slice(0, 7) === thisMonth).length
    const lastShip = shipments.filter(s => s.created_at?.slice(0, 7) === lastMonth).length
    const thisPO   = pos.filter(p => p.created_at?.slice(0, 7) === thisMonth).length
    const lastPO   = pos.filter(p => p.created_at?.slice(0, 7) === lastMonth).length

    const doneOrders = orders.filter(o => o.status === 'done').length
    const completionRate = orders.length > 0 ? pct(doneOrders, orders.length) : 0

    // 近7天趨勢
    const days7 = []
    for (let i = 6; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i)
      const ds = d.toISOString().slice(0, 10)
      days7.push({
        dateStr: ds,
        label: `${d.getMonth()+1}/${d.getDate()}`,
        in:  records.filter(r => r.type === 'in'  && r.created_at?.slice(0, 10) === ds).reduce((a, r) => a + r.qty, 0),
        out: records.filter(r => r.type === 'out' && r.created_at?.slice(0, 10) === ds).reduce((a, r) => a + r.qty, 0),
      })
    }

    return { totalStock, lowStock, thisIn, lastIn, thisOut, lastOut, thisShip, lastShip, thisPO, lastPO, doneOrders, completionRate, days7 }
  }, [products, records, shipments, pos, orders, thisMonth, lastMonth])

  // ── Trend tab data ────────────────────────────────────────────────────────
  const trendData = useMemo(() => {
    const src = trendRecords !== null ? trendRecords : records
    const days = []
    for (let i = trendDays - 1; i >= 0; i--) {
      const d = new Date(); d.setDate(d.getDate() - i)
      const ds = d.toISOString().slice(0, 10)
      days.push({
        dateStr: ds,
        label: `${d.getMonth()+1}/${d.getDate()}`,
        in:  src.filter(r => r.type === 'in'  && r.created_at?.slice(0, 10) === ds).reduce((a, r) => a + r.qty, 0),
        out: src.filter(r => r.type === 'out' && r.created_at?.slice(0, 10) === ds).reduce((a, r) => a + r.qty, 0),
      })
    }
    const totalIn  = days.reduce((a, d) => a + d.in,  0)
    const totalOut = days.reduce((a, d) => a + d.out, 0)
    // Top products by out qty
    const prodMap = {}
    src.filter(r => r.type === 'out').forEach(r => {
      if (!r.product_id) return
      if (!prodMap[r.product_id]) {
        const p = products.find(p => p.id === r.product_id)
        prodMap[r.product_id] = { label: p?.name || r.product_id, value: 0 }
      }
      prodMap[r.product_id].value += r.qty
    })
    const topProducts = Object.values(prodMap).sort((a, b) => b.value - a.value).slice(0, 8)
    return { days, totalIn, totalOut, topProducts }
  }, [trendRecords, records, trendDays, products])

  // ── Shipping tab data ─────────────────────────────────────────────────────
  const shipData = useMemo(() => {
    const months6 = getLast6Months()
    const monthly = months6.map(m => ({
      label: monthLabel(m),
      value: shipments.filter(s => s.created_at?.slice(0, 7) === m).length,
    }))
    // Top customers
    const custMap = {}
    shipments.forEach(s => {
      if (!s.customer_name) return
      custMap[s.customer_name] = (custMap[s.customer_name] || 0) + 1
    })
    const topCustomers = Object.entries(custMap).map(([label, value]) => ({ label, value })).sort((a, b) => b.value - a.value).slice(0, 10)
    // Status breakdown
    const statuses = [
      { label: _('crmShipPending'),   key: 'pending',   color: '#f59e0b' },
      { label: _('crmShipConfirmed'), key: 'confirmed', color: '#3b82f6' },
      { label: _('crmShipShipped'),   key: 'shipped',   color: '#10b981' },
      { label: _('crmShipCancelled'), key: 'cancelled', color: '#94a3b8' },
    ].map(s => ({ ...s, value: shipments.filter(x => x.status === s.key).length }))
      .filter(s => s.value > 0)
    return { monthly, topCustomers, statuses }
  }, [shipments, lang])

  // ── Purchasing tab data ───────────────────────────────────────────────────
  const poData = useMemo(() => {
    const months6 = getLast6Months()
    const monthly = months6.map(m => ({
      label: monthLabel(m),
      value: pos.filter(p => p.created_at?.slice(0, 7) === m).length,
    }))
    const statuses = [
      { label: _('purStatusDraft'),     key: 'draft',     color: '#94a3b8' },
      { label: _('purStatusApproved'),  key: 'approved',  color: '#3b82f6' },
      { label: _('purStatusReceived'),  key: 'received',  color: '#10b981' },
      { label: _('purStatusCancelled'), key: 'cancelled', color: '#ef4444' },
    ].map(s => ({ ...s, value: pos.filter(x => x.status === s.key).length }))
      .filter(s => s.value > 0)
    return { monthly, statuses }
  }, [pos, lang])

  // ── Production tab data ───────────────────────────────────────────────────
  const prodData = useMemo(() => {
    const months6 = getLast6Months()
    const monthly = months6.map(m => ({
      label: monthLabel(m),
      value: orders.filter(o => o.status === 'done' && o.updated_at?.slice(0, 7) === m).length,
    }))
    const statuses = [
      { label: _('prodStatusPending'), key: 'pending',  color: '#94a3b8' },
      { label: _('prodStatusInProd'),  key: 'stage_1',  color: '#3b82f6' },
      { label: _('prodStatusDone'),    key: 'done',     color: '#10b981' },
    ].map(s => ({ ...s, value: orders.filter(o => s.key === 'stage_1'
      ? !['pending','done'].includes(o.status)
      : o.status === s.key
    ).length }))
    const completionRate = orders.length > 0 ? pct(orders.filter(o => o.status === 'done').length, orders.length) : 0
    return { monthly, statuses, completionRate }
  }, [orders, lang])

  // ── Inventory tab data ────────────────────────────────────────────────────
  const invData = useMemo(() => {
    const lowStock = products.filter(p => p.stock <= p.safe_stock)
    // Category breakdown
    const catMap = {}
    products.forEach(p => {
      const k = p.category || '未分類'
      if (!catMap[k]) catMap[k] = { count: 0, stock: 0 }
      catMap[k].count++; catMap[k].stock += p.stock
    })
    const categories = Object.entries(catMap)
      .map(([label, v]) => ({ label, count: v.count, value: v.stock }))
      .sort((a, b) => b.value - a.value)
    // Turnover rate
    const turnover = products.map(p => {
      const outQty = records.filter(r => r.type === 'out' && r.product_id === p.id).reduce((a, r) => a + r.qty, 0)
      const rate = p.stock > 0 ? outQty / p.stock : outQty > 0 ? 99 : 0
      return { label: p.name, value: Math.round(rate * 10) / 10, outQty, stock: p.stock, unit: p.unit }
    }).sort((a, b) => b.value - a.value).slice(0, 10)
    return { lowStock, categories, turnover }
  }, [products, records])

  // ── Maintenance tab data ─────────────────────────────────────────────────
  const maintData = useMemo(() => {
    const TYPE_MAP = {
      breakdown:  { label: _('mntTypeBreakdown'), color: '#ef4444' },
      preventive: { label: _('mntTypePreventive'), color: '#0891b2' },
      scheduled:  { label: _('mntTypeScheduled'), color: '#7c3aed' },
      inspection: { label: _('mntTypeInspection'), color: '#10b981' },
      parts:      { label: _('mntTypeParts'), color: '#f59e0b' },
    }
    const STATUS_MAP = {
      pending:       { label: _('mntStatusPending'), color: '#f59e0b' },
      in_progress:   { label: _('mntStatusInProgress'), color: '#3b82f6' },
      waiting_parts: { label: _('mntStatusWaitParts'), color: '#7c3aed' },
      completed:     { label: _('mntStatusCompleted'), color: '#10b981' },
    }

    // Monthly count (last 6 months)
    const months6 = getLast6Months()
    const byMonth = months6.map(m => ({
      label: monthLabel(m),
      value: maintenance.filter(r => (r.report_date || r.created_at || '').slice(0, 7) === m).length,
    }))

    // By type distribution
    const byType = Object.entries(TYPE_MAP).map(([key, { label, color }]) => ({
      label, color, value: maintenance.filter(r => r.type === key).length,
    })).filter(s => s.value > 0)

    // By status distribution
    const byStatus = Object.entries(STATUS_MAP).map(([key, { label, color }]) => ({
      label, color, value: maintenance.filter(r => r.status === key).length,
    })).filter(s => s.value > 0)

    // Machine frequency rank (top 8)
    const machineCounts = {}
    maintenance.forEach(r => {
      const key = r.machine_name || r.machine_code || '未知設備'
      machineCounts[key] = (machineCounts[key] || 0) + 1
    })
    const byMachine = Object.entries(machineCounts)
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8)

    // Cost summary
    const totalCost = maintenance.reduce((a, r) => a + (Number(r.cost) || 0), 0)
    const thisMCost = maintenance
      .filter(r => (r.report_date || r.created_at || '').slice(0, 7) === thisMonth)
      .reduce((a, r) => a + (Number(r.cost) || 0), 0)
    const lastMCost = maintenance
      .filter(r => (r.report_date || r.created_at || '').slice(0, 7) === lastMonth)
      .reduce((a, r) => a + (Number(r.cost) || 0), 0)

    // Monthly cost (last 6 months)
    const costByMonth = months6.map(m => ({
      label: monthLabel(m),
      value: maintenance
        .filter(r => (r.report_date || r.created_at || '').slice(0, 7) === m)
        .reduce((a, r) => a + (Number(r.cost) || 0), 0),
    }))

    // Active (non-completed) count
    const activeCount = maintenance.filter(r => r.status !== 'completed').length
    const stoppedCount = maintenance.filter(r => r.is_stopped && r.status !== 'completed').length
    const completedThis = maintenance.filter(r => r.status === 'completed' && (r.report_date || r.created_at || '').slice(0, 7) === thisMonth).length
    const completedLast = maintenance.filter(r => r.status === 'completed' && (r.report_date || r.created_at || '').slice(0, 7) === lastMonth).length

    // Recent completed records (last 10)
    const recent = [...maintenance]
      .filter(r => r.status === 'completed')
      .sort((a, b) => (b.report_date || b.created_at || '').localeCompare(a.report_date || a.created_at || ''))
      .slice(0, 10)

    return { byMonth, byType, byStatus, byMachine, totalCost, thisMCost, lastMCost, costByMonth, activeCount, stoppedCount, completedThis, completedLast, recent, TYPE_MAP, STATUS_MAP }
  }, [maintenance, thisMonth, lastMonth, lang])

  // ─────────────────────────────────────────────────────────────────────────
  // Tab config
  const TABS = [
    { key: 'overview',     icon: '📊', label: _('rptTabOverview') },
    { key: 'trend',        icon: '📈', label: _('rptTabInOut') },
    { key: 'shipping',     icon: '🚚', label: _('rptTabShipping') },
    { key: 'purchasing',   icon: '🛒', label: _('rptTabPurchasing') },
    { key: 'production',   icon: '🏭', label: _('rptTabProduction') },
    { key: 'inventory',    icon: '📦', label: _('rptTabInventory') },
    { key: 'maintenance',  icon: '🔧', label: _('rptTabMaintenance') },
  ]

  return (
    <div>
      <PageHeader title={_('rptPageTitle')} en="Reports & Analytics" vi="Báo cáo" subtitle={_('rptSubtitle')} />

      {/* ── KPI 總覽卡片 ─────────────────────────────────────────────────── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
        {[
          {
            icon: '📦', label: _('rptKpiProductTypes'), value: products.length,
            sub: `總庫存 ${fmt(overview.totalStock)} 件`,
            color: C.accent, bg: C.accentDim,
          },
          {
            icon: '⚠️', label: _('rptKpiNeedsRestock'), value: overview.lowStock.length,
            sub: overview.lowStock.length > 0 ? overview.lowStock.slice(0, 2).map(p => p.name).join('、') + (overview.lowStock.length > 2 ? '...' : '') : _('rptKpiSufficient'),
            color: overview.lowStock.length > 0 ? C.red : C.green,
            bg: overview.lowStock.length > 0 ? C.redBg : C.greenBg,
            warn: overview.lowStock.length > 0,
          },
          {
            icon: '📥', label: _('rptKpiInbound'), value: overview.thisIn,
            sub: `上月 ${fmt(overview.lastIn)}`,
            delta: { curr: overview.thisIn, prev: overview.lastIn },
            color: '#0891b2', bg: '#e0f2fe',
          },
          {
            icon: '📤', label: _('rptKpiOutbound'), value: overview.thisOut,
            sub: `上月 ${fmt(overview.lastOut)}`,
            delta: { curr: overview.thisOut, prev: overview.lastOut },
            color: '#7c3aed', bg: '#f5f3ff',
          },
          {
            icon: '🚚', label: _('rptKpiShipments'), value: overview.thisShip,
            sub: `上月 ${fmt(overview.lastShip)}`,
            delta: { curr: overview.thisShip, prev: overview.lastShip },
            color: '#b45309', bg: '#fef3c7',
          },
          {
            icon: '✅', label: _('rptKpiWorkOrderRate'), value: `${overview.completionRate}%`,
            sub: `完成 ${overview.doneOrders} / 共 ${orders.length} 張`,
            color: overview.completionRate >= 80 ? C.green : overview.completionRate >= 50 ? '#f59e0b' : C.red,
            bg: overview.completionRate >= 80 ? C.greenBg : overview.completionRate >= 50 ? '#fffbeb' : C.redBg,
          },
        ].map((k, i) => (
          <div key={i} style={{ background: k.bg, border: `2px solid ${k.color + (k.warn ? '' : '33')}`, borderRadius: 16, padding: '16px 14px', position: 'relative' }}>
            <div style={{ fontSize: 24, marginBottom: 8 }}>{k.icon}</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: k.color, lineHeight: 1 }}>{k.value}</div>
            {k.delta && <DeltaBadge curr={k.delta.curr} prev={k.delta.prev} />}
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginTop: 6 }}>{k.label}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* ── Tab Nav ──────────────────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {TABS.map(t => (
          <button key={t.key}
            style={{ ...st.btn(tab === t.key ? 'primary' : 'default'), padding: '9px 16px', fontSize: 13 }}
            onClick={() => setTab(t.key)}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      {/* ════════════════ TAB: 總覽 ════════════════ */}
      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
          {/* 近7天入出庫 */}
          <SectionCard title={`📅 ${_('rptLast7DaysTitle')}`} subtitle="每日入庫 vs 出庫">
            <Legend items={[{ color: '#10b981', label: _('rptLegendInbound') }, { color: '#ef4444', label: _('rptLegendOutbound') }]} />
            <DualBarChart data={overview.days7} height={120} noDataText={_('rptNoData')} />
            <div style={{ display: 'flex', gap: 10, marginTop: 12 }}>
              {[
                { label: '7天入庫', value: overview.days7.reduce((a, d) => a + d.in, 0), color: '#10b981' },
                { label: '7天出庫', value: overview.days7.reduce((a, d) => a + d.out, 0), color: '#ef4444' },
                { label: '淨變動', value: overview.days7.reduce((a, d) => a + d.in - d.out, 0), color: C.accent },
              ].map(s => (
                <div key={s.label} style={{ flex: 1, background: '#f8fafc', borderRadius: 10, padding: '10px 8px', textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 900, color: s.color }}>{s.value >= 0 ? '' : ''}{fmt(s.value)}</div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 3 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* 分類庫存分佈 */}
          <SectionCard title={`📂 ${_('rptInventoryByCategory')}`} subtitle="各分類庫存數量">
            <RankBars items={invData.categories} color={C.accent} unit=" 件" noDataText={_('rptNoData')} />
          </SectionCard>

          {/* 庫存警示 */}
          <SectionCard
            title={`⚠️ ${_('rptRestockAlertTitle')}`}
            subtitle={overview.lowStock.length > 0 ? `${overview.lowStock.length} 種商品需補貨` : _('rptKpiSufficient')}
          >
            {overview.lowStock.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px', background: C.greenBg, borderRadius: 12 }}>
                <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: C.green }}>{_('rptAllSufficient')}</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {overview.lowStock.slice(0, 6).map(p => (
                  <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: C.redBg, borderRadius: 10, border: `1px solid ${C.redBorder}` }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{p.name}</div>
                      <div style={{ fontSize: 10, color: C.muted }}>安全庫存: {p.safe_stock} {p.unit}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: 20, fontWeight: 900, color: C.red }}>{p.stock}</div>
                      <div style={{ fontSize: 9, color: C.muted }}>{p.unit}</div>
                    </div>
                  </div>
                ))}
                {overview.lowStock.length > 6 && <div style={{ fontSize: 12, color: C.muted, textAlign: 'center' }}>…另有 {overview.lowStock.length - 6} 種</div>}
              </div>
            )}
          </SectionCard>

          {/* 工單狀態 */}
          <SectionCard title={`🏭 ${_('rptWorkOrderStatus')}`} subtitle={`共 ${orders.length} 張工單`}>
            {orders.length === 0 ? (
              <div style={{ color: C.muted, textAlign: 'center', padding: 20 }}>{_('rptNoData')}</div>
            ) : (
              <DonutChart
                centerLabel="工單"
                segments={[
                  { label: _('prodStatusPending'), value: orders.filter(o => o.status === 'pending').length, color: '#94a3b8' },
                  { label: _('prodStatusInProd'),  value: orders.filter(o => !['pending','done'].includes(o.status)).length, color: '#3b82f6' },
                  { label: _('prodStatusDone'),     value: orders.filter(o => o.status === 'done').length, color: '#10b981' },
                ].filter(s => s.value > 0)}
              />
            )}
          </SectionCard>
        </div>
      )}

      {/* ════════════════ TAB: 入出庫趨勢 ════════════════ */}
      {tab === 'trend' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Date range selector */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: C.muted, fontWeight: 600 }}>顯示範圍：</span>
            {[['7', '近7天'], ['14', '近14天'], ['30', '近30天'], ['90', '近3個月']].map(([d, l]) => (
              <button key={d}
                style={{ ...st.btn(trendDays === +d ? 'primary' : 'default'), padding: '7px 14px', fontSize: 12 }}
                onClick={() => setTrendDays(+d)}>
                {l}
              </button>
            ))}
            {trendLoading && <span style={{ fontSize: 12, color: C.muted }}>{_('loading')}</span>}
          </div>

          {/* Main chart */}
          <SectionCard
            title={`📈 近${trendDays}天入出庫趨勢`}
            subtitle="每日入庫數量與出庫數量"
            action={
              <button style={{ ...st.btn('default'), padding: '6px 12px', fontSize: 11 }}
                onClick={() => exportCSV(
                  `入出庫趨勢_${trendDays}天.csv`,
                  ['日期', '入庫', '出庫', '淨變動'],
                  trendData.days.map(d => [d.dateStr, d.in, d.out, d.in - d.out])
                )}>
                📥 匯出
              </button>
            }
          >
            <Legend items={[{ color: '#10b981', label: _('rptLegendInbound') }, { color: '#ef4444', label: _('rptLegendOutbound') }]} />
            <DualBarChart data={trendData.days} height={180} noDataText={_('rptNoData')} />
            {/* Summary */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginTop: 16 }}>
              {[
                { label: `${trendDays}天入庫`, value: trendData.totalIn,  color: '#10b981', bg: '#f0fdf4' },
                { label: `${trendDays}天出庫`, value: trendData.totalOut, color: '#ef4444', bg: '#fff1f2' },
                { label: '淨變動',             value: trendData.totalIn - trendData.totalOut, color: C.accent, bg: C.accentDim },
              ].map(s => (
                <div key={s.label} style={{ background: s.bg, borderRadius: 12, padding: '12px', textAlign: 'center' }}>
                  <div style={{ fontSize: 24, fontWeight: 900, color: s.color }}>{fmt(s.value)}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Top products by outbound */}
          <SectionCard title="🔝 出庫最多商品 TOP 8" subtitle={`近${trendDays}天出庫量排名`}>
            <RankBars items={trendData.topProducts} color='#ef4444' unit=" 件" noDataText={_('rptNoData')} />
          </SectionCard>
        </div>
      )}

      {/* ════════════════ TAB: 出貨分析 ════════════════ */}
      {tab === 'shipping' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
          <SectionCard title={`📅 ${_('rptShippingTitle')}`} subtitle="每月新建出貨單數量">
            <ColBarChart data={shipData.monthly} color='#7c3aed' height={130} noDataText={_('rptNoData')} />
          </SectionCard>

          <SectionCard title="📊 出貨狀態分佈" subtitle={`共 ${shipments.length} 筆出貨記錄`}>
            <DonutChart centerLabel="出貨" segments={shipData.statuses} />
          </SectionCard>

          <SectionCard
            title="🏆 TOP 客戶排名"
            subtitle="依出貨單數量排名"
            action={
              <button style={{ ...st.btn('default'), padding: '6px 12px', fontSize: 11 }}
                onClick={() => exportCSV(
                  '客戶出貨排名.csv',
                  ['客戶名稱', '出貨單數'],
                  shipData.topCustomers.map(c => [c.label, c.value])
                )}>
                📥 匯出
              </button>
            }
          >
            <RankBars items={shipData.topCustomers} color='#7c3aed' unit=" 筆" noDataText={_('rptNoData')} />
          </SectionCard>

          {/* Recent shipments */}
          <SectionCard title="📋 近期出貨記錄" subtitle="最新 10 筆">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {shipments.slice(0, 10).map(s => {
                const ss = {
                  pending:   { label: _('crmShipPending'),   color: '#f59e0b' },
                  confirmed: { label: _('crmShipConfirmed'), color: '#3b82f6' },
                  shipped:   { label: _('crmShipShipped'),   color: '#10b981' },
                  cancelled: { label: _('crmShipCancelled'), color: '#94a3b8' },
                }[s.status] || { label: s.status, color: '#94a3b8' }
                return (
                  <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8fafc', borderRadius: 10, border: `1px solid ${C.border}` }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, fontFamily: 'monospace' }}>{s.shipment_no}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{s.customer_name || '-'} · {formatDate(s.created_at)}</div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: ss.color, background: ss.color + '18', padding: '3px 10px', borderRadius: 20 }}>{ss.label}</span>
                  </div>
                )
              })}
              {shipments.length === 0 && <div style={{ color: C.muted, textAlign: 'center', padding: 20 }}>{_('rptNoData')}</div>}
            </div>
          </SectionCard>
        </div>
      )}

      {/* ════════════════ TAB: 採購分析 ════════════════ */}
      {tab === 'purchasing' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
          <SectionCard title={`📅 ${_('rptPurchasingTitle')}`} subtitle="每月新建採購單數量">
            <ColBarChart data={poData.monthly} color='#0891b2' height={130} noDataText={_('rptNoData')} />
          </SectionCard>

          <SectionCard title="📊 採購狀態分佈" subtitle={`共 ${pos.length} 筆採購記錄`}>
            <DonutChart centerLabel="採購" segments={poData.statuses} />
          </SectionCard>

          {/* Stats summary */}
          <SectionCard title="📌 採購統計" subtitle="歷史採購資料摘要">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {[
                { label: '採購單總數', value: pos.length, color: C.accent },
                { label: '本月採購單', value: pos.filter(p => p.created_at?.slice(0, 7) === thisMonth).length, color: '#0891b2' },
                { label: '待審核',     value: pos.filter(p => p.status === 'draft').length, color: '#f59e0b' },
                { label: '已收貨',     value: pos.filter(p => p.status === 'received').length, color: '#10b981' },
              ].map(s => (
                <div key={s.label} style={{ background: '#f8fafc', borderRadius: 12, padding: '14px', textAlign: 'center', border: `1.5px solid ${C.border}` }}>
                  <div style={{ fontSize: 24, fontWeight: 900, color: s.color }}>{fmt(s.value)}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Recent POs */}
          <SectionCard title="📋 近期採購記錄" subtitle="最新 10 筆">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {pos.slice(0, 10).map(p => {
                const ss = {
                  draft:     { label: _('purStatusDraft'),     color: '#94a3b8' },
                  approved:  { label: _('purStatusApproved'),  color: '#3b82f6' },
                  received:  { label: _('purStatusReceived'),  color: '#10b981' },
                  cancelled: { label: _('purStatusCancelled'), color: '#ef4444' },
                }[p.status] || { label: p.status, color: '#94a3b8' }
                return (
                  <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', background: '#f8fafc', borderRadius: 10, border: `1px solid ${C.border}` }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, fontFamily: 'monospace' }}>{p.po_no}</div>
                      <div style={{ fontSize: 11, color: C.muted }}>{formatDate(p.created_at)}</div>
                    </div>
                    <span style={{ fontSize: 11, fontWeight: 700, color: ss.color, background: ss.color + '18', padding: '3px 10px', borderRadius: 20 }}>{ss.label}</span>
                  </div>
                )
              })}
              {pos.length === 0 && <div style={{ color: C.muted, textAlign: 'center', padding: 20 }}>{_('rptNoData')}</div>}
            </div>
          </SectionCard>
        </div>
      )}

      {/* ════════════════ TAB: 生產效率 ════════════════ */}
      {tab === 'production' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
          {/* Completion gauge */}
          <SectionCard title={`✅ ${_('rptProductionTitle')}`} subtitle={`共 ${orders.length} 張工單`}>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              <div style={{ fontSize: 64, fontWeight: 900, color: prodData.completionRate >= 80 ? '#10b981' : prodData.completionRate >= 50 ? '#f59e0b' : C.red, lineHeight: 1 }}>
                {prodData.completionRate}<span style={{ fontSize: 28 }}>%</span>
              </div>
              <div style={{ fontSize: 13, color: C.muted, marginTop: 8 }}>
                完成 {orders.filter(o => o.status === 'done').length} 張 / 共 {orders.length} 張
              </div>
            </div>
            <DonutChart centerLabel="工單" segments={prodData.statuses.filter(s => s.value > 0)} />
          </SectionCard>

          {/* Monthly completion */}
          <SectionCard title="📅 近6個月完工趨勢" subtitle="每月完成工單數量">
            <ColBarChart data={prodData.monthly} color='#10b981' height={130} noDataText={_('rptNoData')} />
          </SectionCard>

          {/* Work orders detail */}
          <div style={{ ...st.card, gridColumn: 'span 2' }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.text, marginBottom: 14 }}>工單明細 (最新20筆)</div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 480 }}>
                <thead>
                  <tr>
                    {[['工單號','No'],['產品','Product'],['計劃','Plan'],['完成','Done'],['進度','Progress'],['狀態','Status']].map(([zh, en]) => (
                      <th key={zh} style={st.th}><div>{zh}</div><div style={{ fontSize: 9, opacity: 0.45 }}>{en}</div></th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orders.length === 0
                    ? <tr><td colSpan={6} style={{ textAlign: 'center', padding: 32, color: C.muted }}>{_('rptNoData')}</td></tr>
                    : orders.slice(0, 20).map(o => {
                      const rate = pct(o.completed_qty || 0, o.quantity || 1)
                      const isDone = o.status === 'done'
                      return (
                        <tr key={o.id}>
                          <td style={{ ...st.td, fontFamily: 'monospace', color: C.accent, fontWeight: 700, fontSize: 12 }}>{o.order_no}</td>
                          <td style={{ ...st.td, color: C.text, fontWeight: 600 }}>{o.product_name}</td>
                          <td style={st.td}>{fmt(o.quantity)}</td>
                          <td style={{ ...st.td, color: isDone ? '#10b981' : C.muted, fontWeight: isDone ? 700 : 400 }}>{fmt(o.completed_qty || 0)}</td>
                          <td style={st.td}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <div style={{ flex: 1, background: '#e8f0f8', borderRadius: 4, height: 8 }}>
                                <div style={{ width: `${Math.min(rate, 100)}%`, height: '100%', background: rate >= 100 ? '#10b981' : C.accent, borderRadius: 4 }} />
                              </div>
                              <span style={{ fontSize: 11, fontWeight: 700, color: C.sub, flexShrink: 0, width: 30 }}>{rate}%</span>
                            </div>
                          </td>
                          <td style={st.td}>
                            <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: isDone ? '#f0fdf4' : '#eff6ff', color: isDone ? '#10b981' : '#3b82f6' }}>
                              {isDone ? _('prodStatusDone') : o.status === 'pending' ? _('prodStatusPending') : _('prodStatusInProd')}
                            </span>
                          </td>
                        </tr>
                      )
                    })
                  }
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ════════════════ TAB: 庫存分析 ════════════════ */}
      {tab === 'inventory' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>
          {/* Category breakdown */}
          <SectionCard title={`📂 ${_('rptInventoryByCategory')}`} subtitle="各分類現有庫存量">
            <ColBarChart data={invData.categories.slice(0, 8)} color={C.accent} height={130} noDataText={_('rptNoData')} />
          </SectionCard>

          {/* Low stock */}
          <SectionCard
            title={`⚠️ ${_('rptRestockList')}`}
            subtitle={invData.lowStock.length > 0 ? `${invData.lowStock.length} 種商品庫存不足` : _('rptKpiSufficient')}
            action={
              invData.lowStock.length > 0 &&
              <button style={{ ...st.btn('danger'), padding: '6px 12px', fontSize: 11 }}
                onClick={() => exportCSV(
                  '補貨清單.csv',
                  ['商品名稱', 'SKU', '現有庫存', '安全庫存', '單位', '需補數量'],
                  invData.lowStock.map(p => [p.name, p.sku, p.stock, p.safe_stock, p.unit, Math.max(0, p.safe_stock - p.stock)])
                )}>
                📥 {_('rptExportRestock')}
              </button>
            }
          >
            {invData.lowStock.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px', background: C.greenBg, borderRadius: 12, border: `1.5px solid ${C.greenBorder}` }}>
                <div style={{ fontSize: 36, marginBottom: 8 }}>✅</div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.green }}>{_('rptAllSufficient')}</div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {invData.lowStock.map(p => {
                  const ratio = pct(p.stock, p.safe_stock || 1)
                  const need = Math.max(0, p.safe_stock - p.stock)
                  return (
                    <div key={p.id} style={{ background: C.redBg, border: `1.5px solid ${C.redBorder}`, borderRadius: 12, padding: '12px 14px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{p.name}</div>
                          <div style={{ fontSize: 10, color: C.muted }}>SKU: {p.sku} · 需補: <strong style={{ color: C.red }}>{need}</strong> {p.unit}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 22, fontWeight: 900, color: C.red }}>{p.stock}</div>
                          <div style={{ fontSize: 10, color: C.muted }}>安全庫存: {p.safe_stock}</div>
                        </div>
                      </div>
                      <div style={{ background: '#fff', borderRadius: 6, height: 8, overflow: 'hidden' }}>
                        <div style={{ width: `${Math.min(ratio, 100)}%`, height: '100%', background: C.red, borderRadius: 6 }} />
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </SectionCard>

          {/* Turnover rate */}
          <SectionCard
            title={`🔄 ${_('rptInventoryTurnover')}`}
            subtitle="周轉率 = 累計出庫 ÷ 現有庫存"
            action={
              <button style={{ ...st.btn('default'), padding: '6px 12px', fontSize: 11 }}
                onClick={() => exportCSV(
                  '庫存周轉率.csv',
                  ['商品名稱', '現有庫存', '累計出庫', '周轉率', '等級'],
                  invData.turnover.map(t => [t.label, t.stock, t.outQty, t.value.toFixed(2), t.value >= 2 ? '高' : t.value >= 0.5 ? '中' : '低'])
                )}>
                📥 匯出
              </button>
            }
          >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {invData.turnover.length === 0 ? (
                <div style={{ color: C.muted, textAlign: 'center', padding: 20 }}>{_('rptNoData')}</div>
              ) : invData.turnover.map((p, i) => {
                const lvl = p.value >= 2 ? { label: '高', color: '#10b981', bg: '#f0fdf4' } : p.value >= 0.5 ? { label: '中', color: '#f59e0b', bg: '#fffbeb' } : { label: '低', color: '#94a3b8', bg: '#f1f5f9' }
                const maxT = invData.turnover[0]?.value || 1
                return (
                  <div key={p.label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ fontSize: 11, color: i < 3 ? lvl.color : C.muted, fontWeight: i < 3 ? 800 : 400, width: 18, textAlign: 'right', flexShrink: 0 }}>#{i+1}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
                        <span style={{ fontSize: 12, color: C.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>{p.label}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 12, fontWeight: 800, color: lvl.color }}>{p.value.toFixed(1)}x</span>
                          <span style={{ fontSize: 10, fontWeight: 700, color: lvl.color, background: lvl.bg, padding: '1px 6px', borderRadius: 8 }}>{lvl.label}</span>
                        </div>
                      </div>
                      <div style={{ background: '#f1f5f9', borderRadius: 4, height: 6 }}>
                        <div style={{ width: `${(p.value / maxT) * 100}%`, height: '100%', background: lvl.color, borderRadius: 4, transition: 'width 0.4s' }} />
                      </div>
                      <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>庫存 {p.stock} · 出庫 {p.outQty} {p.unit}</div>
                    </div>
                  </div>
                )
              })}
            </div>
          </SectionCard>

          {/* All products summary export */}
          <SectionCard title="📋 庫存全覽匯出" subtitle="匯出全部商品庫存狀態">
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 16 }}>
              {[
                { label: '商品總種數', value: products.length, color: C.accent },
                { label: '總庫存量',   value: products.reduce((a, p) => a + p.stock, 0), color: '#0891b2' },
                { label: '零庫存商品', value: products.filter(p => p.stock === 0).length, color: C.red },
                { label: '正常庫存',   value: products.filter(p => p.stock > p.safe_stock).length, color: C.green },
              ].map(s => (
                <div key={s.label} style={{ background: '#f8fafc', borderRadius: 10, padding: '12px', textAlign: 'center', border: `1.5px solid ${C.border}` }}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: s.color }}>{fmt(s.value)}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>{s.label}</div>
                </div>
              ))}
            </div>
            <button
              style={{ ...st.btn(), width: '100%', padding: '12px' }}
              onClick={() => exportCSV(
                `庫存全覽_${new Date().toLocaleDateString('zh-TW').replace(/\//g,'-')}.csv`,
                ['商品名稱', 'SKU', '分類', '現有庫存', '安全庫存', '單位', '狀態'],
                products.map(p => [p.name, p.sku, p.category || '未分類', p.stock, p.safe_stock, p.unit, p.stock === 0 ? '零庫存' : p.stock <= p.safe_stock ? '需補貨' : '正常'])
              )}>
              📥 {_('rptExportInventory')}
            </button>
          </SectionCard>
        </div>
      )}

      {/* ════════════════ TAB: 維修報表 ════════════════ */}
      {tab === 'maintenance' && (
        <div>
          {/* KPI 卡片 */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { icon: '🔧', label: _('rptMaintTotal'),      value: maintenance.length,        color: '#7c3aed', bg: '#ede9fe', sub: `本月完成 ${maintData.completedThis} 件` },
              { icon: '⚙️', label: _('rptMaintProcessing'), value: maintData.activeCount,     color: '#3b82f6', bg: '#eff6ff', sub: '未完成工單' },
              { icon: '🔴', label: _('rptMaintStopped'),    value: maintData.stoppedCount,    color: '#ef4444', bg: '#fee2e2', sub: '需優先處理', warn: maintData.stoppedCount > 0 },
              { icon: '💰', label: _('rptMaintCost'),       value: `NT$${fmt(maintData.totalCost)}`, color: '#f59e0b', bg: '#fffbeb', sub: `本月 NT$${fmt(maintData.thisMCost)}` },
            ].map(c => (
              <div key={c.label} style={{ background: c.bg, border: `1.5px solid ${c.color}33`, borderRadius: 14, padding: '14px 16px' }}>
                <div style={{ fontSize: 22 }}>{c.icon}</div>
                <div style={{ fontSize: c.value.toString().length > 8 ? 16 : 22, fontWeight: 900, color: c.color, marginTop: 4, lineHeight: 1 }}>{c.value}</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: c.color, marginTop: 4 }}>{c.label}</div>
                <div style={{ fontSize: 10, color: '#64748b', marginTop: 2 }}>{c.sub}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16 }}>

            {/* Monthly count trend */}
            <SectionCard title={`📅 ${_('rptMonthlyRepair')}`} subtitle="近 6 個月維修工單數">
              <ColBarChart data={maintData.byMonth} color="#7c3aed" height={130} noDataText={_('rptNoData')} />
            </SectionCard>

            {/* Monthly cost trend */}
            <SectionCard
              title="💰 每月維修費用"
              subtitle="近 6 個月費用趨勢（NT$）"
              action={
                <DeltaBadge curr={maintData.thisMCost} prev={maintData.lastMCost} />
              }
            >
              <ColBarChart data={maintData.costByMonth} color="#f59e0b" height={130} noDataText={_('rptNoData')} />
            </SectionCard>

            {/* Type distribution */}
            <SectionCard title={`🗂️ ${_('rptRepairTypeDist')}`} subtitle="按維修類型統計">
              {maintData.byType.length > 0
                ? <DonutChart segments={maintData.byType} centerLabel="維修類型" />
                : <div style={{ color: C.muted, fontSize: 13, padding: '16px 0' }}>{_('rptNoData')}</div>
              }
            </SectionCard>

            {/* Status distribution */}
            <SectionCard title="📋 工單狀態分布" subtitle="目前各狀態件數">
              {maintData.byStatus.length > 0
                ? <DonutChart segments={maintData.byStatus} centerLabel="工單狀態" />
                : <div style={{ color: C.muted, fontSize: 13, padding: '16px 0' }}>{_('rptNoData')}</div>
              }
            </SectionCard>

            {/* Machine frequency ranking */}
            <SectionCard title={`🏆 ${_('rptMachineFailRanking')}`} subtitle="維修次數最多的設備 TOP 8">
              {maintData.byMachine.length > 0
                ? <RankBars items={maintData.byMachine} color="#ef4444" unit="次" noDataText={_('rptNoData')} />
                : <div style={{ color: C.muted, fontSize: 13, padding: '16px 0' }}>{_('rptNoData')}</div>
              }
            </SectionCard>

            {/* Recent completed records + CSV export */}
            <SectionCard
              title={`✅ ${_('rptRecentCompleted')}`}
              subtitle="最新 10 筆已完成維修"
              action={
                <button style={{ ...st.btn('default'), padding: '6px 12px', fontSize: 11 }}
                  onClick={() => exportCSV(
                    `維修紀錄_${new Date().toLocaleDateString('zh-TW').replace(/\//g, '-')}.csv`,
                    ['日期', '設備名稱', '設備編號', '類型', '狀態', '費用(NT$)', '停機'],
                    maintenance.map(r => [
                      r.report_date || (r.created_at || '').slice(0, 10),
                      r.machine_name || '',
                      r.machine_code || '',
                      maintData.TYPE_MAP[r.type]?.label || r.type,
                      maintData.STATUS_MAP[r.status]?.label || r.status,
                      Number(r.cost) || 0,
                      r.is_stopped ? '是' : '否',
                    ])
                  )}>
                  📥 {_('rptExportAll')}
                </button>
              }
            >
              {maintData.recent.length === 0 ? (
                <div style={{ color: C.muted, fontSize: 13, padding: '16px 0' }}>{_('rptNoCompletedRepairs')}</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {maintData.recent.map(r => {
                    const t = maintData.TYPE_MAP[r.type] || { label: r.type, color: '#94a3b8' }
                    return (
                      <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px', background: '#f8fafc', borderRadius: 10, border: `1px solid ${C.border}` }}>
                        <div style={{ width: 6, height: 36, borderRadius: 3, background: t.color, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.machine_name || r.machine_code || '未知設備'}</div>
                          <div style={{ fontSize: 10, color: C.muted }}>{r.report_date || (r.created_at || '').slice(0, 10)}</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 3, flexShrink: 0 }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: t.color, background: `${t.color}18`, padding: '2px 7px', borderRadius: 8 }}>{t.label}</span>
                          {Number(r.cost) > 0 && <span style={{ fontSize: 10, color: '#f59e0b', fontWeight: 600 }}>NT${fmt(Number(r.cost))}</span>}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </SectionCard>

          </div>
        </div>
      )}
    </div>
  )
}
